<!-- StartNewImprintForm.vue - Form for creating new imprint with Pinia integration -->
<template>
  <div class="start-new-imprint-form">
    <!-- 1. Choose an Imprint Method (Type: Screenprint, Embroidery, etc) -->
    <ImprintMethodSelector
      :key="`method-${imprintKey}`"
      :initial-method="imprintStore.selectedImprintMethod?.sku"
      :imprint-index="imprintKey"
      @method-selected="handleImprintMethodSelected"
    />

    <!-- 2. Choose a Location -->
    <ImprintLocationSelector
      :key="`location-${imprintKey}`"
      label="2. Choose a Location"
      :initial-location="newImprintStore.imprints[imprintKey]?.selectedLocation?.sku"
      :imprint-key="imprintKey"
      :auto-select-first="imprintKey === 0"
      :show-help="true"
      :has-error="!!(currentImprint.errors && currentImprint.errors.location)"
      :error-message="currentImprint.errors?.location"
      @location-selected="handleLocationSelected"
    />

    <!-- 3. Add Your Imprint Message (hidden for Envelop products) -->
    <div v-if="!isEnvelopProduct" class="form-section">
      <label class="section-label">3. Add Your Imprint Message</label>

      <!-- Message Type Selection -->
      <div class="message-type-buttons">
        <BaseButton
          v-for="type in messageTypes"
          :key="type.id"
          :text="type.label"
          :variant="currentImprint.messageType === type.id ? 'primary' : 'secondary'"
          size="small"
          @click="(e) => selectMessageType(type.id, e)"
        />
      </div>

      <!-- Text Lines Section (for Text Only or Text & Image) -->
      <div v-if="shouldShowTextLines" class="text-lines-section">
        <!-- Max Lines Warning -->
        <div v-if="currentImprint.errors?.maxLines" class="warning-message">
          <p>{{ currentImprint.errors.maxLines }}</p>
        </div>

        <!-- Text Line Component -->
        <div
          v-for="(line, index) in textLinesFiltered"
          :key="`line-${imprintKey}-${line.id}`"
          class="text-line"
        >
          <div class="line-header">
            <span class="line-label">Line {{ index + 1 }}{{ index > 0 ? ' (Optional)' : '' }}</span>
          </div>

          <div class="line-content">
            <!-- Color Selector -->
            <div class="color-column">
              <div 
                class="color-swatch"
                :class="{ 'has-error': currentImprint.errors && currentImprint.errors[`line${line.id}_color`] }"
                :data-error-field="`imprint-${imprintKey}-line-${index}-color`"
                @click="toggleColorPicker(line.id)"
              >
                <div
                  v-if="line.selectedColor && !line.customColor"
                  class="color-preview"
                  :style="{ backgroundImage: `url(${getColorImageUrl(line.selectedColor)})` }"
                ></div>
                <div
                  v-else-if="line.customColor"
                  class="custom-color-preview"
                  :data-full-text="line.selectedColor"
                >
                  {{ line.selectedColor }}
                </div>
                <div v-else class="no-color-preview"></div>
              </div>

              <span v-if="line.selectedColorObj && !line.customColor" class="color-name">
                {{ line.selectedColorObj.desc }}
              </span>
            </div>

            <!-- Text Input -->
            <div class="text-column">
              <input
                type="text"
                :value="line.message"
                :class="{ 'has-error': currentImprint.errors && currentImprint.errors[`line${line.id}_message`] }"
                :data-error-field="`imprint-${imprintKey}-line-${index}-text`"
                placeholder="Enter text"
                @input="handleLineTextChange(line.id, $event.target.value)"
                @focus="handleTextLineFocus(line.id)"
              />
            </div>
          </div>

          <!-- Color Picker Dropdown (2-column layout) -->
          <div v-if="line.showColorPicker" class="color-picker-dropdown">
            <!-- Right Column: Color Info and Actions -->
            <div class="color-picker-right">
              <div class="color-info-header">Line {{ index + 1 }} Imprint {{ getColorNameText }}</div>
              <div class="color-info-name">
                {{ getDisplayColorName(line) }}
              </div>
              
              <!-- Apply to All Lines Checkbox -->
              <div class="apply-to-all-lines">
                <label>
                  <input
                    type="checkbox"
                    :checked="isApplyToAllActive(line.id)"
                    :disabled="(!line.selectedColor && !isApplyToAllActive(line.id)) ||
                        (currentImprint.applyToAll.active && currentImprint.applyToAll.sourceLineId !== line.id)"
                    @change="handleApplyToAll(line.id)"
                  />
                  Apply to all lines
                </label>
              </div>

                <div class="imprint-colour-actions">
                    <a
                        class="btn-clear-colour"
                        @click="clearAndCloseColor(line.id)"
                    >
                        Clear & Close
                    </a>
                </div>

            </div>

            <!-- Left Column: Color Palette and Pantone -->
            <div class="color-picker-left">
              <div class="color-grid">
                <button
                  v-for="color in availableColorsForLine(line)"
                  :key="color.sku"
                  class="color-option"
                  :class="{ 
                    'is-pending': isPendingColor(line.id, color.sku),
                    'is-selected': isSelectedColor(line.id, color.sku),
                    'is-disabled': color.disabled
                  }"
                  :style="{ backgroundImage: `url(${getColorImageUrl(color.file_name)})` }"
                  :title="color.disabled ? `${color.desc} (max colours reached)` : color.desc"
                  @click="selectPendingColor(line.id, color)"
                ></button>
              </div>

              <div class="color-divider">— or —</div>

              <!-- Custom Color Input (Pantone) -->
              <div class="custom-color-section">
                <!-- Standard Presets + Saved PANTONE (combined) -->
                <div class="custom-color-presets">
                  <button
                    v-for="preset in customColorPresets"
                    :key="preset"
                    class="custom-color-preset"
                    :class="{ 
                      'is-pending': isPendingPantone(line.id, preset),
                      'is-selected': isSelectedPantone(line.id, preset)
                    }"
                    @click="selectPendingPantone(line.id, preset)"
                  >
                    {{ preset }}
                  </button>
                  <!-- Saved Pantone Colors (same style, same row) -->
                  <button
                    v-for="pantone in savedPantoneColors"
                    :key="pantone"
                    class="custom-color-preset"
                    :class="{ 
                      'is-pending': isPendingPantone(line.id, pantone),
                      'is-selected': isSelectedPantone(line.id, pantone)
                    }"
                    @click="selectPendingPantone(line.id, pantone)"
                  >
                    {{ pantone }}
                  </button>
                </div>

                <!-- Pantone Input with Submit Button -->
                <div class="pantone-input-wrapper">
                  <input
                    type="text"
                    :ref="`pantone_${line.id}`"
                    placeholder="Enter PANTONE© name"
                    class="pantone-input"
                    :value="pantoneInputs[line.id] || ''"
                    @input="handlePantoneInput(line.id, $event.target.value)"
                    @keyup.enter="submitPantone(line.id)"
                  />
                  <button
                    class="pantone-submit-btn"
                    :disabled="!((pantoneInputs[line.id] || '').trim())"
                    @click="submitPantone(line.id)"
                  >
                    submit
                  </button>
                </div>
                <div class="pantone-fee">${{ newImprintStore.customColorFee.toFixed(2) }}</div>
              </div>
            </div>
          </div>

          <p v-if="currentImprint.errors && currentImprint.errors[`line${line.id}_color`]" class="error-message">
            {{ currentImprint.errors[`line${line.id}_color`] }}
          </p>
          <p v-if="currentImprint.errors && currentImprint.errors[`line${line.id}_message`]" class="error-message">
            {{ currentImprint.errors[`line${line.id}_message`] }}
          </p>
        </div>
        
        <!-- Note: All lines are automatically shown based on location's maxLines -->
        <!-- No "Add Line" button needed on desktop -->
      </div>

      <!-- Image Upload Section (for Image Only or Text & Image) -->
      <div v-show="shouldShowImageUpload" class="image-upload-section"
           :class="{ 'has-error': currentImprint.errors && currentImprint.errors.image }"
           :data-error-field="`imprint-${imprintKey}-image-upload`">
        <ArtworkUpload
          :initial-image-data="currentImprint.imageData"
          @upload-success="handleImageUploadSuccess"
          @upload-error="handleImageUploadError"
          @file-removed="handleImageRemoved"
        />

        <!-- Image Color Selection -->
        <!-- CRITICAL: Show ONLY for Text & Image (NOT for Image Only) -->
        <div v-if="imageLineData && (currentImprint.messageType === 'text_image'|| currentImprint.messageType === 'image')" class="image-color-section">
          <label>Image {{getColorNameText}}</label>
          <div class="color-selector">
            <!-- Use button for reliable click detection -->
            <button
              type="button"
              class="color-swatch image-color-swatch"
              :class="{ 'has-error': currentImprint.errors && currentImprint.errors.imageColor }"
              :data-error-field="`imprint-${imprintKey}-image-color`"
              @click.prevent="toggleColorPicker(imageLineData.id)"
            >
              <div
                v-if="imageLineData.selectedColor && !imageLineData.customColor"
                class="color-preview"
                :style="{ backgroundImage: `url(${getColorImageUrl(imageLineData.selectedColor)})` }"
              ></div>
              <div
                v-else-if="imageLineData.customColor"
                class="custom-color-preview"
              >
                {{ imageLineData.selectedColor }}
              </div>
              <div v-else class="no-color-preview"></div>
            </button>

            <span v-if="imageLineData.selectedColorObj && !imageLineData.customColor" class="color-name">
              {{ imageLineData.selectedColorObj.desc }}
            </span>
          </div>

          <!-- Color Picker for Image — always open for Image Only and Text & Image modes -->
          <div class="color-picker-dropdown">
            <!-- Right Column: Color Info and Actions -->
            <div class="color-picker-right">
              <div class="color-info-header">Image Imprint {{ getColorNameText }}</div>
              <div class="color-info-name">
                {{ getDisplayColorName(imageLineData) }}
              </div>

              <div class="imprint-colour-actions">
                <a
                  class="btn-clear-colour"
                  @click="clearAndCloseColor(imageLineData.id)"
                >
                  Clear Selection
                </a>
              </div>
            </div>

            <!-- Left Column: Color Palette -->
            <div class="color-picker-left">
              <!-- Show message if no colors available -->
              <div v-if="availableColorsForLine(imageLineData).length === 0" class="no-colors-message">
                No colours available. Please select an imprint location first.
              </div>
              <div v-else class="color-grid">
                <button
                  v-for="color in availableColorsForLine(imageLineData)"
                  :key="color.sku"
                  class="color-option"
                  :class="{ 
                    'is-pending': isPendingColor(imageLineData.id, color.sku),
                    'is-selected': isSelectedColor(imageLineData.id, color.sku),
                    'is-disabled': color.disabled
                  }"
                  :style="{ backgroundImage: `url(${getColorImageUrl(color.file_name)})` }"
                  :title="color.disabled ? `${color.desc} (max colours reached)` : color.desc"
                  @click="selectPendingColor(imageLineData.id, color)"
                ></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Special Instructions -->
    <div v-if="debugConfig.showSpecialInstructionsStep2" class="form-section">
      <label class="section-label">Special Instructions</label>
      <textarea
        :value="currentImprint.specialInstructions"
        rows="5"
        placeholder="Enter any special instructions here..."
        @input="handleSpecialInstructionsChange($event.target.value)"
      ></textarea>
    </div>

    <!-- Validation Errors Summary -->
<!--    <div v-if="hasErrors" class="validation-errors">
      <p class="error-summary">
        Please ensure each line has been filled out and has a color selected before proceeding.
      </p>
    </div>-->
  </div>
</template>

<script>
import BaseButton from './BaseButton.vue';
import ImprintMethodSelector from './ImprintMethodSelector.vue';
import ImprintLocationSelector from './ImprintLocationSelector.vue';
import ArtworkUpload from './ArtworkUpload.vue';
import { useImprintStore } from '../stores/useImprintStore';
import { useNewImprintStore } from '../stores/useNewImprintStore';
import { useAppStore } from '../stores/useAppStore';
import { DEBUG_CONFIG } from '../config/debug.js';
import {getColorName} from '../static_data/sections';

export default {
  name: 'StartNewImprintForm',

  components: {
    BaseButton,
    ImprintMethodSelector,
    ImprintLocationSelector,
    ArtworkUpload
  },

  props: {
    // Imprint key to work with specific imprint in store
    imprintKey: {
      type: Number,
      default: 0
    }
  },

  setup(props) {
    const imprintStore = useImprintStore();
    const newImprintStore = useNewImprintStore();
    const appStore = useAppStore();
    
    // REMOVED: Don't switch automatically - let each component use its own imprintKey prop
    // if (props.imprintKey !== newImprintStore.currentImprintKey) {
    //   newImprintStore.switchToImprint(props.imprintKey);
    // }
    
    return {
      imprintStore,
      newImprintStore,
      appStore,
      debugConfig: DEBUG_CONFIG
    };
  },

  data() {
    return {
      // Pantone input values per line (line.id -> string)
      pantoneInputs: {},

      // Message Types
      messageTypes: [
        { id: 'text', label: 'Text Only' },
        { id: 'image', label: 'Image Only' },
        { id: 'text_image', label: 'Text & Image' }
      ],

      // Colors
      colorCatalogBaseUrl: 'https://imprint-uploads.s3.ca-central-1.amazonaws.com/assets/images/imprint_app/colors/',
      customColorPresets: [] //'Full Color', 'CMYK'
    };
  },

  computed: {
    // Current imprint data from store
    // IMPORTANT: Use imprintKey prop to ensure correct imprint for this component instance
    currentImprint() {
      return this.newImprintStore.imprints[this.imprintKey] || {};
    },

    getColorNameText(){
        return getColorName();
    },

    // Filtered text lines (only message type, not image)
    textLinesFiltered() {
      return (this.currentImprint.textLines || []).filter(l => l.type === 'message');
    },

    // Check if should show text lines section
    shouldShowTextLines() {
      return this.currentImprint.messageType === 'text' || 
             this.currentImprint.messageType === 'text_image';
    },

    // Check if should show image upload section
    shouldShowImageUpload() {
      return this.currentImprint.messageType === 'image' || 
             this.currentImprint.messageType === 'text_image';
    },

    // Get image line data
    imageLineData() {
      const lines = this.currentImprint.textLines || [];
      const imageLine = lines.find(line => line.type === 'image') || null;
      console.log('[StartNewImprintForm] imageLineData computed:', {
        linesCount: lines.length,
        lineTypes: lines.map(l => l.type),
        imageLineFound: !!imageLine,
        imageLineId: imageLine?.id
      });
      return imageLine;
    },

    // Check if has any validation errors
    hasErrors() {
      return Object.keys(this.currentImprint.errors || {}).length > 0;
    },

    // Check if this is an Envelop product (hide imprint message section)
    isEnvelopProduct() {
      return this.appStore.isEnvelopProduct;
    },

    // Note: canAddLine computed removed - all lines are automatically shown based on maxLines

    // Get pending color selection for current imprint
    pendingColorSelection() {
      return this.currentImprint.pendingColorSelection || {};
    },

    // Get saved Pantone colors for current imprint
    savedPantoneColors() {
      return this.currentImprint.savedPantoneColors || [];
    },

    // Check if color is currently pending for a specific line
    isPendingColor() {
      return (lineId, colorSku) => {
        const pending = this.pendingColorSelection;
        return pending.lineId === lineId && 
               pending.colorObj?.sku === colorSku;
      };
    },

    // Check if Pantone is currently pending for a specific line
    isPendingPantone() {
      return (lineId, pantoneValue) => {
        const pending = this.pendingColorSelection;
        return pending.lineId === lineId && 
               pending.customColor && 
               pending.color === pantoneValue;
      };
    },

    // NEW: Check if color is currently selected (applied) for a specific line
    isSelectedColor() {
      return (lineId, colorSku) => {
        const line = this.currentImprint.textLines?.find(l => l.id === lineId);
        if (!line || line.customColor) return false;
        return line.selectedColorObj?.sku === colorSku;
      };
    },

    // NEW: Check if Pantone is currently selected (applied) for a specific line
    isSelectedPantone() {
      return (lineId, pantoneValue) => {
        const line = this.currentImprint.textLines?.find(l => l.id === lineId);
        if (!line || !line.customColor) return false;
        return line.selectedColor === pantoneValue;
      };
    },

    // NEW: Get display name for color (pending or applied)
    getDisplayColorName() {
      return (line) => {
        // If there's a pending color for this line, show it
        if (this.pendingColorSelection.lineId === line.id) {
          if (this.pendingColorSelection.customColor) {
            // Pending Pantone - show full text
            return this.pendingColorSelection.color;
          } else if (this.pendingColorSelection.colorObj) {
            // Pending regular color
            return this.pendingColorSelection.colorObj.desc;
          }
        }

        // Otherwise show the currently applied color
        if (line.customColor && line.selectedColor) {
          // Applied Pantone - show full text
          return line.selectedColor;
        } else if (line.selectedColorObj) {
          // Applied regular color
          return line.selectedColorObj.desc;
        }

        // No color selected
        return `(${getColorName()} Name)`;
      };
    }
  },

  // REMOVED: watch imprintKey - causes conflicts with multiple imprints
  // Each component should use its own imprintKey prop directly
  // watch: {
  //   imprintKey: {
  //     immediate: true,
  //     handler(newKey) {
  //       if (newKey !== this.newImprintStore.currentImprintKey) {
  //         this.newImprintStore.switchToImprint(newKey);
  //       }
  //     }
  //   }
  // },

  watch: {
    // Auto-select single colour when an image line becomes available
    // (fires when user switches to Image Only or Text & Image)
    imageLineData(newVal) {
      if (newVal && !newVal.selectedColor) {
        this.$nextTick(() => {
          this.autoSelectIfSingleColor(newVal.id);
        });
      }
    }
  },

  methods: {
    handlePantoneInput(lineId, value) {
          this.pantoneInputs[lineId] = value;
    },
    // Check if Apply to All is active for specific line
    isApplyToAllActive(lineId) {
      return this.currentImprint.applyToAll?.active && 
             this.currentImprint.applyToAll?.sourceLineId === lineId;
    },

    // Imprint Method Selection
    handleImprintMethodSelected(method) {
      console.log('[StartNewImprintForm] Imprint method selected:', method);
      
      // ✅ NEW: Set active method in store (preserves data when switching)
      this.newImprintStore.setActiveImprintMethod(method.sku, this.imprintKey);
      
      this.$emit('method-changed', method);
    },

    // Location Selection
    handleLocationSelected(data) {
      console.log('[DEBUG handleLocationSelected] START - data:', data, 'imprintKey:', this.imprintKey);
      
      if (!data || !data.sku) {
        console.log('[DEBUG handleLocationSelected] ABORT - No data or SKU');
        return;
      }
      
      // Find full location object
      const location = this.newImprintStore.availableLocations.find(loc => loc.sku === data.sku);
      console.log('[DEBUG handleLocationSelected] Found location:', location?.title, 'imprint_lines:', location?.imprint_lines);
      
      if (location) {
        console.log('[DEBUG handleLocationSelected] Calling setLocation with imprintKey:', this.imprintKey);
        this.newImprintStore.setLocation(location, this.imprintKey);
        
        console.log('[DEBUG handleLocationSelected] AFTER setLocation - textLines count:', this.currentImprint.textLines?.length);
        this.$emit('location-changed', location);
      } else {
        console.log('[DEBUG handleLocationSelected] ERROR - Location not found');
      }
    },

    // Message Type Selection
    selectMessageType(typeId, event) {
      console.log('%c[MSG-TYPE-CLICK] selectMessageType', 'background: darkred; color: white; font-weight: bold', {
        typeId,
        imprintKey: this.imprintKey,
        currentMessageType: this.currentImprint?.messageType,
        activeMethodSku: this.newImprintStore.imprints[this.imprintKey]?.activeMethodSku,
        // isTrusted=true = real user click; isTrusted=false = programmatic/synthetic click
        isTrusted: event?.isTrusted,
        targetTag: event?.target?.tagName,
        targetClass: event?.target?.className,
      });

      // Block synthetic (programmatic) clicks — only real user clicks should change message type
      // This prevents Vue reactivity re-renders from triggering unwanted messageType changes
      if (event && !event.isTrusted) {
        console.error('%c[MSG-TYPE-CLICK] \u26a0\ufe0f SYNTHETIC CLICK BLOCKED (isTrusted=false)', 'background: red; color: white; font-size: 14px');
        return;
      }

      this.newImprintStore.setMessageType(typeId, this.imprintKey);
      this.$emit('message-type-changed', typeId);
    },

    // Text Line Management - Lines are auto-managed by setLocation() in store
    // addTextLine() and removeLine() removed - not needed on desktop (all lines shown automatically)

    handleLineTextChange(lineId, text) {
      this.newImprintStore.updateLineText(lineId, text, this.imprintKey);
      this.$emit('lines-changed', this.currentImprint.textLines);
    },

    // Color Selection - NEW FLOW with Add Colour button
    toggleColorPicker(lineId) {
      console.log('[StartNewImprintForm] toggleColorPicker called:', {
        lineId,
        imprintKey: this.imprintKey,
        imprint: this.currentImprint?.textLines?.map(l => ({ id: l.id, type: l.type, showColorPicker: l.showColorPicker }))
      });
      this.newImprintStore.toggleColorPicker(lineId, this.imprintKey);
    },

    // Select color and apply immediately (Add Colour button removed)
    selectPendingColor(lineId, color) {
      if (color.disabled) return; // Colour beyond the max-colours limit for this line
      this.newImprintStore.selectPendingColor(lineId, color, false, this.imprintKey);
      this.applyPendingColor(lineId);
    },

    // Select Pantone and apply immediately (Add Colour button removed)
    selectPendingPantone(lineId, pantoneValue) {
      this.newImprintStore.selectPendingColor(lineId, pantoneValue, true, this.imprintKey);
      this.applyPendingColor(lineId);
    },

    // Apply pending color (triggered by Add Colour button)
    applyPendingColor(lineId) {
      this.newImprintStore.applyPendingColor(lineId, this.imprintKey);
      this.$emit('lines-changed', this.currentImprint.textLines);
    },

    // Submit new Pantone color (save to list)
    submitPantone(lineId) {
      const inputRef = this.$refs[`pantone_${lineId}`];
      if (!inputRef || !inputRef[0]) return;
      
      //const pantoneValue = inputRef[0].value.trim();
      // Use reactive value (source of truth for enabling/disabling button)
      const pantoneValue = (this.pantoneInputs[lineId] || '').trim();

      if (!pantoneValue) {
        alert('Please enter a PANTONE number');
        return;
      }

      // Check if already exists
      if (this.savedPantoneColors.includes(pantoneValue)) {
        alert(`PANTONE ${pantoneValue} already exists in your color list`);
        return;
      }

      // Add to saved Pantone colors
      this.newImprintStore.addSavedPantoneColor(pantoneValue, this.imprintKey);
      
      // Also select it as pending
      this.selectPendingPantone(lineId, pantoneValue);
      
      // Show success message
      const successMsg = `✅ PANTONE ${pantoneValue} added to your color list ($50.00 fee)`;
      console.log(successMsg);
      
      // You can add a toast/notification here instead of alert
      // For now, just log to console

      // Clear input (both DOM + reactive state)
      this.pantoneInputs[lineId] = '';
      inputRef[0].value = '';
    },

    // Apply to All - FIXED: Now passes imprintKey explicitly
    handleApplyToAll(sourceLineId) {
      this.newImprintStore.toggleApplyToAll(sourceLineId, this.imprintKey);
      this.$emit('lines-changed', this.currentImprint.textLines);
    },

    // Open colour picker when user focuses a text line input — always, regardless of
    // whether a colour has already been selected. The colour swatch toggle is separate.
    handleTextLineFocus(lineId) {
      const line = this.currentImprint?.textLines?.find(l => l.id === lineId);
      if (!line) return;

      this.newImprintStore.openColorPicker(lineId, this.imprintKey);
      this.autoSelectIfSingleColor(lineId);
    },

    // If only one colour is available for the given line and no colour selected yet,
    // select and apply it automatically so the user doesn't have to click.
    autoSelectIfSingleColor(lineId) {
      const line = this.currentImprint?.textLines?.find(l => l.id === lineId);
      if (!line || line.selectedColor) return; // Already has a colour

      const colors = this.availableColorsForLine(line).filter(c => !c.disabled);
      if (colors.length !== 1) return; // Not a single-colour situation

      const singleColor = colors[0];
      // Select and immediately apply the only available colour
      this.newImprintStore.selectPendingColor(lineId, singleColor, false, this.imprintKey);
      this.newImprintStore.applyPendingColor(lineId, this.imprintKey);
      this.$emit('lines-changed', this.currentImprint.textLines);
      console.log('[StartNewImprintForm] Auto-selected single colour for line', lineId, ':', singleColor.desc);
    },

      clearAndCloseColor(lineId) {
          const imprint = this.currentImprint;

          // Find the current text line by id
          const line = imprint.textLines.find(l => l.id === lineId);
          if (!line) return;

          const isSourceApplyToAll =
              imprint.applyToAll.active && imprint.applyToAll.sourceLineId === lineId;

          // 1) If this line is the source for "Apply to all",
          // disable ApplyToAll and clear colors from ALL lines
          if (isSourceApplyToAll) {
              // Disable ApplyToAll and reset source line
              imprint.applyToAll.active = false;
              imprint.applyToAll.sourceLineId = null;

              // Clear color data from all text lines
              imprint.textLines.forEach(l => {
                  l.selectedColor = null;
                  l.selectedColorObj = null;
                  l.customColor = false;
              });
          } else {
              // 2) Otherwise, clear color only for the current line
              line.selectedColor = null;
              line.selectedColorObj = null;
              line.customColor = false;
          }

          // 3) Clear pending color selection
          // (prevents checkbox and picker logic from being blocked)
          imprint.pendingColorSelection = {
              lineId: null,
              color: null,
              colorObj: null,
              customColor: false
          };

          // 4) Close the color picker panel
          line.showColorPicker = false;

          // 5) (Optional) Remove color-related validation error for this line
          delete imprint.errors?.[`line${lineId}_color`];

          // 6) (Optional but recommended)
          // Recalculate unique colors count if you track max colors / pricing
          this.newImprintStore.recalculateUniqueColors(this.imprintKey);

          // 7) If UI depends on lines-changed to refresh (price/summary), emit it
          this.$emit('lines-changed', imprint.textLines);
      },

      // Get available colors for a specific line
    // Takes into account max colors logic and removes duplicates
    availableColorsForLine(line) {
      const allColors = this.newImprintStore.availableColors;
      
      // Filter out duplicates by SKU - keep first occurrence
      const uniqueColorsBySku = [];
      const seenSkus = new Set();
      
      allColors.forEach(color => {
        if (!seenSkus.has(color.sku)) {
          seenSkus.add(color.sku);
          uniqueColorsBySku.push(color);
        }
      });
      
      // Get max colors tracking for current imprint
      const maxColorsTracking = this.currentImprint.maxColorsTracking || {};
      const isMaxReached = maxColorsTracking.isMaxReached || false;
      const uniqueColors = maxColorsTracking.uniqueColors || [];

      if (!isMaxReached || line.selectedColor) {
        return uniqueColorsBySku.map(color => ({ ...color, disabled: false }));
      }

      return uniqueColorsBySku.map(color => ({
        ...color,
        disabled: !uniqueColors.includes(color.sku)
      }));
    },

    getColorImageUrl(fileName) {
      return `${this.colorCatalogBaseUrl}${fileName}.png`;
    },

    // Image Upload
    handleImageUploadSuccess(data) {
      // CRITICAL FIX: Add filename from file.name for cart payload
      const imageData = {
        ...data,
        filename: data.file?.name || 'uploaded_image'
      };
      
      this.newImprintStore.setImageData(imageData, this.imprintKey);
      this.$emit('image-uploaded', imageData);
    },

    handleImageUploadError(error) {
      console.error('[StartNewImprintForm] Image upload failed:', error);
    },

    handleImageRemoved() {
      this.newImprintStore.setImageData(null, this.imprintKey);
      this.$emit('image-removed');
    },

    // Special Instructions
    handleSpecialInstructionsChange(text) {
      this.newImprintStore.setSpecialInstructions(text, this.imprintKey);
      this.$emit('special-instructions-changed', text);
    },

    // Validation
    validate() {
      return this.newImprintStore.validate(this.imprintKey);
    },

    // Get form data for submission
    getData() {
      return this.newImprintStore.getImprintData(this.imprintKey);
    }
  },

  mounted() {
    // Initialize from product data when mounted
    this.newImprintStore.initializeFromProductData();

    // Auto-select single colour for image line if it already exists on mount
    // (e.g. when restoring state after a redirect)
    this.$nextTick(() => {
      if (this.imageLineData && !this.imageLineData.selectedColor) {
        this.autoSelectIfSingleColor(this.imageLineData.id);
      }
    });

    console.log('[StartNewImprintForm] Mounted with Pinia stores:', {
      imprintStore: this.imprintStore,
      newImprintStore: this.newImprintStore,
      currentImprint: this.currentImprint,
      maxColors: this.newImprintStore.currentImprint?.maxColorsTracking?.maxColors
    });
  }
};

// Export for use in other components
export { };
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;
.start-new-imprint-form {
  width: 100%;
}

a.btn-clear-colour {
    font-size: 14px;
    font-style: italic;
    text-decoration: underline;

    &:hover {
        cursor: pointer;
    }
}

// Form Section
.form-section {
  margin-bottom: 30px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  > .section-label {
    padding: 0;
  }
}

.section-label {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #000;
  margin-bottom: 12px;
}

// Message Type Buttons
.message-type-buttons {
  display: flex;
  justify-content: space-between; // Space between buttons
  gap: 12px;
  flex-wrap: nowrap; // Don't wrap to next line
  margin-bottom: 20px;
  
  :deep(button) {
    flex: 1; // Equal width buttons
    max-width: 32%; // About 30% each with gaps
    min-width: 0; // Allow shrinking if needed
  }

  :deep(.btn-text) {
    font-weight: 600;
  }

  @media (max-width: 560px) {
    button {
      padding: 5px 5px
    }
  }
}

// Text Lines Section
// Text Lines Section
.text-lines-section {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px; // Figma: spacing between lines
    background: #FFFFFF;
    width: 100%;
}

.text-line {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 5px; // Figma: spacing between label and input row
}

.line-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-bottom: 0;
    gap: 8px;

    .line-label {
        font-weight: 400;   // Figma: 400
        font-size: 16px;    // Figma: 16
        line-height: 22px;  // Figma: 22
        color: #898989;     // Figma
    }
}

.line-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0;
    width: 100%;
    height: 40px;        // Figma: 40
    min-height: 40px;
    border: 1px solid $color-border; // Border must be on the container, not on input
    border-radius: 5px;
    overflow: hidden;
    background: #FFFFFF;
    transition: border-color 0.2s ease;

    &:focus-within {
        border-color: $brand-secondary;
    }

    // Apply error border if either color or text has error
    &:has(.has-error) {
        border-color: #FF0000;
    }
}

.color-column {
    width: 40px;         // Figma: 40
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: #FFFFFF;
    border-right: 1px solid #D9D9D9;
}

.color-swatch {
    width: 40px;
    height: 40px;
    border: 1px solid #D9D9D9;
    background: #FFFFFF;
    border-radius: 0;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: $brand-secondary;
    }

    &.has-error {
        border-color: transparent;
    }

    // Arrow removed (not in Figma)
}

.color-preview {
    width: 20px; // Figma: 20x20
    height: 20px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 2px;
}

.no-color-preview {
    width: 20px;
    height: 20px;
    background: #D9D9D9;
    border-radius: 2px;
}

.text-column {
    flex: 1;
    display: flex;
    align-items: center; // Keep the input area vertically centered inside the row
}

/* --- Theme override: remove input border and force true vertical centering --- */
:deep(.line-content .text-column input[type="text"]) {
    /* Remove theme border/background (container has the border) */
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    outline: none !important;

    /* Match Figma control size */
    height: 40px !important;
    min-height: 40px !important;

    /* Optical vertical centering (baseline compensation) */
    line-height: 38px !important;
    padding: 1px 16px !important; // 1px top/bottom keeps text visually centered

    /* Typography */
    width: 100% !important;
    font-size: 16px !important;
    box-sizing: border-box !important;

    &.has-error {
        background: #FFF5F5 !important;
    }
}

:deep(.line-content .text-column input[type="text"]::placeholder) {
    color: #999 !important;
    line-height: 38px !important;
}

/* --- Fix theme input margins inside imprint text lines --- */
:deep(.line-content .text-column input[type="text"]) {
    margin: 0 !important;          // Remove theme margin completely
}

// Color Picker Dropdown (2-column layout)
/* Make dropdown responsive: width 100%, height auto (depends on swatches count) */
.color-picker-dropdown {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    width: 100%;
    max-width: 100%;
    min-height: 0;     /* allow auto height */
    height: auto;

    padding: 20px;
    gap: 20px;

    background: #FFFFFF;
    border: 1px solid #D9D9D9;
    border-radius: 5px;
    box-shadow: -4px 4px 4px rgba(0, 0, 0, 0.25);
    box-sizing: border-box;

    overflow: visible;
}

/* Vertical divider between left panel and right content */
.color-picker-dropdown::before {
    content: '';
    position: absolute;
    top: 20px;
    bottom: 20px;

    /* left padding (20) + left panel width (200) + half of the gap (10) */
    left: calc(20px + 200px + 10px);

    width: 1px;
    background: #D9D9D9;
}

/* Theme resets inside dropdown */

/* Left panel */
.color-picker-right {
    width: 200px;     /* Figma panel width */
    flex-shrink: 0;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    padding: 0;
    border: none;
    background: transparent;
    box-sizing: border-box;
}

/* Keep the header block compact */
.color-info-header {
    font-family: $font-family-base;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    color: #333333;
}

.color-info-name {
    font-family: $font-family-base;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: #898989;
}

/* Checkbox row */
.apply-to-all-lines {
    width: 100%;

    label {
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: $font-family-base;
        font-weight: 400;
        font-size: 14px;
        line-height: 19px;
        color: #898989;
        user-select: none;
        white-space: nowrap;
    }

    input[type='checkbox'] {
        width: 13px;
        height: 13px;
        border-radius: 2px;
    }
}

/* Add Colour button: single line, fixed height */
.color-picker-right :deep(.base-button) {
    min-width: 104px !important; /* safe: prevents wrapping */
    height: 24px !important;
    padding: 5px 10px !important;
    border-radius: 3px !important;
    //background: #0D72B1 !important;
    //border: 1px solid #0054A3 !important;
}

.color-picker-right :deep(.base-button .btn-text) {
    font-family: 'Open Sans', sans-serif !important;
    font-weight: 400 !important;
    font-size: 10px !important;
    line-height: 14px !important;
    color: #FFFFFF !important;
    white-space: nowrap !important; /* keep "Add Colour" in one line */
}

/* Right content area */
.color-picker-left {
    flex: 1;
    min-width: 0;

    display: flex;
    flex-direction: column;
    gap: 10px;

    width: 100%;
    height: auto;
}

/* Swatches area: responsive width and auto height */
.color-grid {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    gap: 2px;

    width: 100%;
    min-height: 0;
    height: auto;
}

/* Swatch button uses inline background-image; keep 20x20 centered */
.color-option {
    width: 30px;
    height: 30px;

    padding: 0 !important;
    margin: 0 !important;

    border: none !important;
    border-radius: 0;

    background-color: transparent !important;
    background-repeat: no-repeat !important;
    background-position: center !important;
    background-size: 20px 20px !important;

    cursor: pointer;
    position: relative;

    box-shadow: none !important;
    outline: none !important;
}

/* Inner 20x20 border (Figma) */
.color-option::after {
    content: '';
    position: absolute;
    inset: 5px;
    border: 1px solid #D9D9D9;
    border-radius: 2px;
    pointer-events: none;
}

/* Pending/active swatch highlight */
.color-option.is-pending {
    box-shadow: 0 0 0 3px #0D72B1 !important;
    z-index: 1;
}

.color-option.is-disabled {
    opacity: 0.25;
    cursor: default;
    filter: grayscale(100%);
}

.color-option.is-selected::before {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #28a745;
    font-size: 18px;
    font-weight: bold;
    text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff;
    pointer-events: none;
}

/* Divider "— or —" */
.color-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;

    font-family: $font-family-base;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #898989;
    width: 100%;
}

.color-divider::before,
.color-divider::after {
    content: '';
    width: 20px;
    height: 1px;
    background: #D9D9D9;
}

/* Presets */
.custom-color-presets {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

button.custom-color-preset {
    border: 1px solid #c1c1c1;
    background: #fff;
    padding: 3px;
    margin-bottom: 3px;
}

/* Hover */
.custom-color-preset:hover {
    border-color: #0D72B1;
    background: #f0f8ff;
}

/* Pending (як color-option.is-pending) */
.custom-color-preset.is-pending {
    border-color: #0D72B1;
    background: #E6F3FF;
    font-weight: 600;
}

/* Selected (як було раніше) */
.custom-color-preset.is-selected {
    border-color: #28a745;
    background: #d4edda;
    font-weight: 600;
    padding-left: 24px;
}

/* Checkmark */
/*.custom-color-preset.is-selected::before {
    content: '✓';
    position: absolute;
    left: 6px;
    top: 50%;
    transform: translateY(-50%);
    color: #28a745;
    font-weight: bold;
}*/

/* Pantone row: responsive width */
.pantone-input-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;

    width: 100%;
    min-height: 40px;

    padding: 0 5px;
    border: 1px solid #D9D9D9;
    border-radius: 5px;
    box-sizing: border-box;
}

.pantone-input {
    flex: 1;
    height: 38px;

    border: none !important;
    outline: none !important;
    background: transparent !important;

    padding: 0 8px;
    font-family: $font-family-base;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #333333;
    min-width: 0;
}

.pantone-input::placeholder {
    color: #898989;
}

.pantone-submit-btn {
    height: 30px;
    padding: 0 12px;
    border-radius: 3px;
    border: $brand-secondary;
    background: $brand-primary;
    color: #FFFFFF;

    font-family: $font-family-base;
    font-weight: 400;
    font-size: 12px;
    line-height: 30px;

    cursor: pointer;
    white-space: nowrap;
    &:disabled {
        opacity: .5;
        cursor: not-allowed;
        pointer-events: none;
    }
}

/* Fee */
.pantone-fee {
    width: 100%;
    text-align: right;
    font-weight: 600;
    color: #333333;
    margin-top: 4px;
}

// Add Line Button
.add-line-btn {
  display: flex;
  align-items: center;
  align-self: flex-start; // Don't stretch to full width
  gap: 8px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #0D72B1;
  border-radius: 5px;
  color: #0D72B1;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  
  &:hover {
    background: #f0f8ff;
  }
  
  .plus-icon {
    font-size: 18px;
  }
}

// Image Upload Section
.image-upload-section {
  margin-top: 20px;

  // Red border on upload zone when image not uploaded
  &.has-error :deep(.dropzone) {
    border-color: #FF0000;
    border-style: solid;
  }
}

.no-colors-message {
  padding: 12px;
  color: #898989;
  font-size: 14px;
  font-style: italic;
}

// Mobile: image-only color picker stacks vertically (matches Text & Image look)
@media (max-width: 600px) {
  .image-color-section {
    .color-picker-dropdown {
      flex-direction: column;
      gap: 16px;

      &::before {
        display: none;
      }

      .color-picker-right {
        width: 100%;
      }

      .color-picker-left {
        width: 100%;
      }
    }
  }
}

.image-color-section {
  margin-top: 16px;
  
  label {
    display: block;
    font-size: 14px;
    font-weight: 400;
    color: $color-black;
    margin-bottom: 8px;
  }
  
  .color-selector {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  // Override button defaults - looks identical to div swatch
  button.image-color-swatch {
    width: 40px !important;
    height: 40px !important;
    flex-shrink: 0;
    padding: 0 !important;
    margin: 0 !important;
    border: 1px solid #D9D9D9 !important;
    background: #FFFFFF !important;
    border-radius: 0 !important;
    cursor: pointer !important;
    pointer-events: auto !important;
    position: relative;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-shadow: none !important;
    outline: none !important;
    appearance: none !important;

    &:hover {
      border-color: #0D72B1 !important;
    }

    // Red border when image colour not selected
    &.has-error {
      border-color: #FF0000 !important;
    }
  }
}

// Special Instructions
.form-section {
  textarea {
    width: 100%;
    padding: 12px;
    font-size: 16px;
    font-family: $font-family-base;
    border: 1px solid #D9D9D9;
    border-radius: 5px;
    resize: vertical;
    
    &:focus {
      outline: none;
      border-color: $brand-primary;
    }
  }
}

// Error Messages
.error-message {
  color: $color-danger;
  font-size: 12px;
  margin: 4px 0 0 0;
}

.validation-errors {
  padding: 12px;
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
  
  .error-summary {
    color: #856404;
    font-size: 14px;
    margin: 0;
  }
}
.color-column .custom-color-preview {
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
